import PatientOverview from "./PatientOverview";
import UnitOrders from "./UnitOrders";
import { incidentCode } from "../domain/incidentProfiles";
import { useEffect, useState } from "react";
import {
  MapPin,
  UsersRound,
  Camera,
  ArrowUpRight,
  Check,
  X,
  Plus,
  ClipboardList,
  ShieldCheck,
  Activity,
  ChevronRight,
  Radio,
} from "lucide-react";
import {
  type Incident,
  type SimState,
  phaseLabels,
  isOpen,
  incidentLabels,
  statusLabels,
} from "../domain/model";
import { coverage } from "../domain/engine";
import { time, eta } from "../format";
import { vehicleCategory } from "./DispatchMap";
import type { Perform } from "./ResourceManager";
export default function IncidentInspector({
  state,
  incident,
  act,
  enabled,
  onFocus,
  onDispatch,
  onCam,
  onMeasures,
  onRadio,
}: {
  state: SimState;
  incident?: Incident;
  act: Perform;
  enabled: boolean;
  onFocus: () => void;
  onDispatch: () => void;
  onCam: () => void;
  onMeasures: () => void;
  onRadio: () => void;
}) {
  const [tab, setTab] = useState("details"),
    [note, setNote] = useState("");
  useEffect(() => {
    setTab("details");
    setNote("");
  }, [incident?.id]);
  if (!incident)
    return (
      <aside className="inspector">
        <div className="empty">
          <ClipboardList size={30} />
          <h2>Kein Einsatz ausgewählt</h2>
          <p>Wähle einen Einsatz auf der Karte oder in der Einsatzliste.</p>
        </div>
      </aside>
    );
  const i = incident,
    assigned = state.vehicles.filter((v) => v.incidentId === i.id),
    stabilized = i.phase === "stabilisiert",
    archived = !isOpen(i);
  return (
    <aside className="inspector">
      <section className={`incident-summary ${i.type}`}>
        <div className="incident-summary-main">
          <span className={`incident-code ${i.type}`}>{incidentCode(i)}</span>
          <div className="incident-summary-title">
            <span>{incidentLabels[i.type].category}</span>
            <h1>{i.title}</h1>
          </div>
          <div className="incident-age">
            <b>{time(i.createdAt).slice(0, 5)}</b>
            <small>
              seit {Math.max(0, Math.floor((state.now - i.createdAt) / 60000))}{" "}
              Min.
            </small>
          </div>
        </div>
        <div className="incident-location">
          <MapPin size={16} />
          <div>
            {i.address}
            <small>{i.district}</small>
          </div>
        </div>
        <div
          className="inspector-tabs"
          role="tablist"
          aria-label="Einsatzdetails"
        >
          {[
            ["details", "Details"],
            ["units", "Kräfte"],
            ["messages", "Meldungen"],
            ["scene", "Lagebild"],
          ].map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              aria-controls="inspector-tabpanel"
              onClick={() => setTab(id)}
            >
              {label}
              {id === "messages" &&
                i.timeline.some((e) => e.type === "warning") && <i />}
            </button>
          ))}
        </div>
      </section>
      <div className="inspector-content">
        <section
          className="incident-info"
          id="inspector-tabpanel"
          role="tabpanel"
          aria-label={
            tab === "details"
              ? "Details"
              : tab === "units"
                ? "Kräfte"
                : tab === "messages"
                  ? "Meldungen"
                  : "Lagebild"
          }
        >
          {tab === "details" ? (
            <>
              <div className="info-block">
                <span className="field-label">Einsatzstichwort</span>
                <p>
                  {`${incidentCode(i)} – ${i.title}`}
                  <span className="case-id">E {i.number}</span>
                </p>
              </div>
              <div className="info-block">
                <span className="field-label">Beschreibung</span>
                <p className="report">{i.report}</p>
              </div>
              <div className="info-block">
                <span className="field-label">Anrufer · fiktiv</span>
                <p>{i.callerName ?? "Nicht erfasst"}</p>
                {i.addressSource ? (
                  <p className="muted small">
                    Adresse aus OpenStreetMap ·{" "}
                    {i.addressSource.accuracy === "house"
                      ? "Straße und Hausnummer"
                      : "Straße ohne Hausnummer"}{" "}
                    ·{" "}
                    <a
                      href={i.addressSource.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Quelle ansehen
                    </a>
                  </p>
                ) : (
                  <p className="muted small">
                    Älterer Einsatz · Adresse noch nicht mit dem Kartenbestand
                    abgeglichen.
                  </p>
                )}
              </div>
              <div className="info-block">
                <span className="field-label">Koordinaten</span>
                <p className="coordinate-value">
                  {i.location[0].toFixed(4)}, {i.location[1].toFixed(4)}
                  <span className={`phase ${i.phase}`}>
                    <i />
                    {phaseLabels[i.phase]}
                  </span>
                </p>
                <button
                  className="button secondary full-width"
                  onClick={onFocus}
                >
                  <MapPin size={16} />
                  In Kartenansicht zeigen
                </button>
              </div>
              <PatientOverview state={state} incident={i} />
              {i.escalation > 0 && !stabilized && !archived && (
                <div className="escalation-box">
                  <Activity size={15} />
                  <span>
                    Lageverschärfung Stufe {i.escalation}
                    <small>
                      {
                        i.timeline.filter((e) => e.type === "warning").at(-1)
                          ?.text
                      }
                    </small>
                  </span>
                </div>
              )}
            </>
          ) : tab === "units" ? (
            <div className="info-block">
              <span className="field-label">Erforderliche Einsatzmittel</span>
              <div className="requirements">
                {coverage(state, i).map((r) => (
                  <div
                    key={r.kind}
                    className={r.count >= r.needed ? "met" : ""}
                  >
                    <span>{r.kind}</span>
                    <b>
                      {r.count}
                      <em> / {r.needed}</em>
                    </b>
                    {r.count >= r.needed ? (
                      <Check size={14} />
                    ) : (
                      <span className="req-dot" />
                    )}
                  </div>
                ))}
              </div>
              <p className="muted small">
                Der Einsatz wird bearbeitet, sobald alle erforderlichen Kräfte
                an der Einsatzstelle sind.
              </p>
            </div>
          ) : tab === "messages" ? (
            <div className="info-block">
              <div className="journal-caption">
                <span className="field-label">Lage & Maßnahmen</span>
                <span className="count-badge">{i.timeline.length}</span>
              </div>
              <div className="timeline">
                {[...i.timeline].reverse().map((e) => (
                  <div className={`timeline-item ${e.type}`} key={e.id}>
                    <i />
                    <time>{time(e.at).slice(0, 5)}</time>
                    <p>{e.text}</p>
                  </div>
                ))}
              </div>
              <form
                className="note-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (
                    await act(
                      { type: "note", incidentId: i.id, text: note },
                      "Lagenotiz gespeichert.",
                    )
                  )
                    setNote("");
                }}
              >
                <input
                  aria-label="Lagenotiz"
                  placeholder="Lagenotiz hinzufügen …"
                  value={note}
                  maxLength={1000}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button
                  aria-label="Lagenotiz speichern"
                  disabled={!enabled || !note.trim()}
                >
                  <Plus size={18} />
                </button>
              </form>
            </div>
          ) : (
            <div className="info-block">
              <div className="cam-surface compact">
                <Camera size={32} />
                <b>SimCam</b>
                <span>Verkehrskamera · Drohne · Smartphone</span>
              </div>
              <p className="muted small">
                KI-Lagebilder passend zum Einsatz automatisch erstellen oder
                eine Perspektive gezielt anfordern. Alle Aufnahmen sind als
                Simulation gekennzeichnet.
              </p>
              <button className="button secondary full-width" onClick={onCam}>
                <Camera size={16} />
                Lagebild anfordern <ArrowUpRight size={15} />
              </button>
            </div>
          )}
        </section>
        <section className="assigned-section">
          <div className="assigned-title">
            <h2>Zugewiesene Kräfte</h2>
            <span className="count-badge">{assigned.length}</span>
          </div>
          {assigned.length ? (
            <div className="assigned-list">
              {assigned.map((v) => (
                <div className="assigned-unit" key={v.id}>
                  <i className={`service-dot ${vehicleCategory(v)}`} />
                  <span className="assigned-name">
                    {v.name}
                    <small>
                      {v.kind}
                      {state.speechRequests.some((r) => r.vehicleId === v.id)
                        ? " · S5 Sprechwunsch"
                        : ""}
                    </small>
                  </span>
                  <span className={`unit-state status-${v.status}`}>
                    {v.journey?.routeStatus === "error"
                      ? "Route fehlt"
                      : v.journey?.routeStatus === "pending"
                        ? "Routenplanung"
                        : v.journey?.departureAt &&
                            state.now < v.journey.departureAt
                          ? "Rückt aus"
                          : v.status === 3
                            ? "Auf Anfahrt"
                            : v.status === 4
                              ? "Vor Ort"
                              : statusLabels[v.status]}
                  </span>
                  <span
                    className="assigned-eta"
                    title={
                      v.journey?.routeError ??
                      (v.journey?.distanceKm !== undefined
                        ? `${v.journey.distanceKm.toFixed(1)} km · geschätzte Ankunft in Simulationszeit, ohne Live-Verkehr`
                        : undefined)
                    }
                  >
                    {v.journey
                      ? `${v.journey.routeStatus === "ready" ? "ETA " : ""}${eta(v, state.now)}`
                      : "Status " + v.status}
                  </span>
                  <button
                    className="radio-unit-button"
                    title="Status anfordern"
                    aria-label={`${v.name}: Status anfordern`}
                    disabled={!enabled}
                    onClick={() =>
                      void act(
                        { type: "requestStatus", vehicleId: v.id },
                        "Statusabfrage im Funkprotokoll.",
                      )
                    }
                  >
                    <Radio size={14} />
                  </button>
                  <UnitOrders
                    state={state}
                    vehicle={v}
                    act={act}
                    enabled={enabled}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="no-units">
              <UsersRound size={24} />
              <p>
                {archived
                  ? "Alle Kräfte freigegeben."
                  : "Noch keine Kräfte alarmiert."}
                <small>
                  {archived
                    ? "Der Einsatz liegt im Archiv."
                    : "Wähle verfügbare Fahrzeuge für diesen Einsatz."}
                </small>
              </p>
            </div>
          )}
          {state.speechRequests.some((r) => r.incidentId === i.id) && (
            <button
              className="button secondary incident-speech-link"
              onClick={onRadio}
            >
              Sprechwunsch / Rückmeldung bearbeiten
            </button>
          )}
          <div className="inspector-actions">
            <button
              className="button secondary"
              onClick={onDispatch}
              disabled={!enabled || stabilized || archived}
            >
              <UsersRound size={15} />
              {assigned.length
                ? "Weitere Kräfte alarmieren"
                : "Kräfte alarmieren"}
            </button>
            {stabilized ? (
              <button
                className="button success"
                disabled={
                  !enabled ||
                  state.speechRequests.some(
                    (r) => r.incidentId === i.id && r.reason === "completion",
                  )
                }
                onClick={() =>
                  act(
                    { type: "close", incidentId: i.id },
                    "Einsatz abgeschlossen. Kräfte kehren zurück.",
                  )
                }
              >
                <Check size={16} />
                Abschließen
              </button>
            ) : (
              <button
                className="button secondary"
                disabled={!enabled || archived}
                onClick={onMeasures}
              >
                <ShieldCheck size={15} />
                Maßnahmen
              </button>
            )}
          </div>
        </section>
      </div>
      <button
        className="incident-switch"
        onClick={() => setTab(tab === "messages" ? "details" : "messages")}
      >
        <Activity size={15} />
        <span>
          {i.timeline.at(-1)?.type === "warning"
            ? "Neue Lagemeldung"
            : "Letzte Rückmeldung"}
          <small>{i.timeline.at(-1)?.text}</small>
        </span>
        <ChevronRight size={16} />
      </button>
    </aside>
  );
}
